import { categoryRank, type HelpCategoryId } from "./categories";
import { assertUniqueSlugs, validateArticle, type HelpArticleSetting } from "./validate";

export interface HelpArticle {
  slug: string;
  title: string;
  category: HelpCategoryId;
  summary: string;
  keywords: string[];
  order: number;
  updated: string;
  /** Shown only while this portal setting is on. useHelpArticles applies it. */
  requires: HelpArticleSetting | null;
  body: string;
  /** Estimated from the body — deliberately not an authored field. */
  readMinutes: number;
}

/**
 * Content is bundled at build time rather than fetched at runtime.
 *
 * That keeps articles versioned with the deploy, reviewable in a PR, and
 * removes any need for the Static Web Apps navigationFallback to special-case
 * a .md extension. Adding an article is adding a file here — no route change,
 * no navigation change, no footer edit.
 *
 * Validation runs again here, but the build is where a bad article should be
 * caught: the Vite plugin in vite.config.ts checks the same rules through the
 * same module, so a malformed article fails CI rather than white-screening the
 * portal on load.
 */
const files = import.meta.glob("../../content/help/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

const WORDS_PER_MINUTE = 200;

function estimateReadMinutes(body: string): number {
  const words = body.trim().split(/\s+/).length;
  // Round up, never down. Rounding tells someone a 1.9 minute article is a
  // "1 min read", and under-promising the time is the wrong way to be wrong.
  return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE));
}

function build(): HelpArticle[] {
  const parsed = Object.entries(files).map(([path, source]) => {
    const file = path.split("/").pop() ?? path;
    return { file, article: validateArticle(file, source) };
  });

  assertUniqueSlugs(
    parsed.map(({ file, article }) => ({ file, slug: article.slug }))
  );

  return parsed
    .map(({ article }) => ({
      ...article,
      readMinutes: estimateReadMinutes(article.body),
    }))
    .sort(
      (a, b) =>
        categoryRank(a.category) - categoryRank(b.category) ||
        a.order - b.order ||
        a.title.localeCompare(b.title)
    );
}

export const helpArticles: HelpArticle[] = build();

const bySlug = new Map(helpArticles.map((a) => [a.slug, a]));

export function getHelpArticle(slug: string | undefined): HelpArticle | null {
  return slug ? (bySlug.get(slug) ?? null) : null;
}

export function helpArticlesByCategory(
  articles: HelpArticle[],
  category: HelpCategoryId
): HelpArticle[] {
  return articles.filter((a) => a.category === category);
}

/**
 * Neighbours in reading order, for the previous/next control at the foot of an
 * article. Order is the registry's own sort: category, then `order`, then title.
 */
export function adjacentHelpArticles(
  articles: HelpArticle[],
  slug: string
): {
  previous: HelpArticle | null;
  next: HelpArticle | null;
} {
  const index = articles.findIndex((a) => a.slug === slug);
  if (index === -1) return { previous: null, next: null };

  return {
    previous: articles[index - 1] ?? null,
    next: articles[index + 1] ?? null,
  };
}
